/**
 * Lumyn Product Gallery
 * Interactive thumbnail gallery with scroll navigation for product pages
 */

class LumynProductGallery extends HTMLElement {
  constructor() {
    super();
    this.mainImage = null;
    this.thumbsContainer = null;
    this.thumbs = [];
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.mainImage = this.querySelector('[data-main-image]');
    this.thumbsContainer = this.querySelector('[data-thumbs]');
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.prevButton = this.querySelector('[data-thumb-prev]');
    this.nextButton = this.querySelector('[data-thumb-next]');
    this.thumbsWrapper = this.querySelector('[data-thumbs-wrapper]');

    if (!this.mainImage || !this.thumbsContainer || this.thumbs.length === 0) {
      return;
    }

    this.bindEvents();
    this.updateArrowVisibility();
    this.setupResizeObserver();

    // Check if a specific variant is requested via URL
    const hasVariantParam = new URLSearchParams(window.location.search).has('variant');

    if (hasVariantParam) {
      // Initialize state from server-rendered active thumbnail
      const activeIndex = this.thumbs.findIndex(thumb => thumb.classList.contains('is-active'));
      if (activeIndex !== -1) {
        this.currentIndex = activeIndex;
        // Ensure the active thumbnail is visible on load
        requestAnimationFrame(() => {
          this.scrollThumbIntoView(this.thumbs[activeIndex]);
        });
      }
    } else {
      // Default to the first image if no variant is explicitly requested
      if (this.thumbs.length > 0) {
        this.selectThumb(0);
      }
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  bindEvents() {
    // Thumbnail click events
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.selectThumb(index));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectThumb(index);
        }
      });
    });

    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollThumbs('prev'));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollThumbs('next'));
    }

    // Update arrows on scroll
    this.thumbsContainer.addEventListener('scroll', () => {
      this.updateArrowVisibility();
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateThumb(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateThumb(1);
      }
    });

    // Touch swipe support for mobile
    this.setupTouchSupport();
  }

  setupTouchSupport() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.mainImage.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.mainImage.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) < swipeThreshold) return;

    if (diff > 0) {
      // Swipe left - next image
      this.navigateThumb(1);
    } else {
      // Swipe right - previous image
      this.navigateThumb(-1);
    }
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateArrowVisibility();
    });
    this.resizeObserver.observe(this.thumbsContainer);
  }

  selectThumb(index) {
    if (index < 0 || index >= this.thumbs.length) return;

    this.currentIndex = index;
    const thumb = this.thumbs[index];
    const mediaId = thumb.dataset.mediaId;
    const mediaSrc = thumb.dataset.mediaSrc;
    const mediaAlt = thumb.dataset.mediaAlt || '';
    const mediaType = thumb.dataset.mediaType || 'image';

    // Update active state
    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    // Update main image
    this.updateMainImage(mediaSrc, mediaAlt, mediaType, mediaId);

    // Scroll thumb into view
    this.scrollThumbIntoView(thumb);

    // Announce change for screen readers
    this.announceChange(index);
  }

  updateMainImage(src, alt, type, mediaId) {
    const currentMedia = this.mainImage.querySelector('img, video');

    if (type === 'image') {
      if (currentMedia && currentMedia.tagName === 'IMG') {
        currentMedia.src = src;
        currentMedia.alt = alt;
      } else {
        this.mainImage.innerHTML = `<img src="${src}" alt="${alt}" loading="eager">`;
      }
    } else if (type === 'video' || type === 'external_video') {
      // Handle video media
      const videoElement = document.querySelector(`[data-media-id="${mediaId}"] video`);
      if (videoElement) {
        this.mainImage.innerHTML = '';
        const clonedVideo = videoElement.cloneNode(true);
        this.mainImage.appendChild(clonedVideo);
        clonedVideo.play();
      }
    }
  }

  navigateThumb(direction) {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.thumbs.length) {
      this.selectThumb(newIndex);
    }
  }

  scrollThumbs(direction) {
    const scrollAmount = this.thumbsContainer.clientWidth * 0.75;
    const currentScroll = this.thumbsContainer.scrollLeft;

    const newScroll = direction === 'prev'
      ? currentScroll - scrollAmount
      : currentScroll + scrollAmount;

    this.thumbsContainer.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }

  scrollThumbIntoView(thumb) {
    const containerRect = this.thumbsContainer.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    const isVisible =
      thumbRect.left >= containerRect.left &&
      thumbRect.right <= containerRect.right;

    if (!isVisible) {
      thumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  updateArrowVisibility() {
    if (!this.thumbsWrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.thumbsContainer;
    const hasOverflow = scrollWidth > clientWidth + 1; // +1 for rounding tolerance
    const atStart = scrollLeft <= 1; // tolerance for rounding
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    // Set data attribute to control arrow container visibility
    // Arrows should be hidden entirely only when no scrolling is possible
    this.thumbsWrapper.dataset.scrollHidden = !hasOverflow;

    // When arrows are visible, disable at boundaries but never hide
    if (this.prevButton) {
      this.prevButton.disabled = atStart;
      this.prevButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  announceChange(index) {
    const statusElement = this.querySelector('[data-gallery-status]');
    if (statusElement) {
      statusElement.textContent = `Image ${index + 1} of ${this.thumbs.length}`;
    }
  }
}

// Register the custom element
if (!customElements.get('lumyn-product-gallery')) {
  customElements.define('lumyn-product-gallery', LumynProductGallery);
}

/**
 * Smooth scroll for "Learn More" button
 * Prevents navbar state changes during scroll by dispatching preventHeaderReveal
 */
document.addEventListener('DOMContentLoaded', () => {
  const learnMoreButtons = document.querySelectorAll('[data-scroll-to]');

  learnMoreButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = button.dataset.scrollTo;
      const targetElement = document.querySelector(targetId);

      if (targetElement) {
        // Prevent sticky header from changing state during scroll
        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }

        // Get header height for offset calculation
        const header = document.querySelector('.header-wrapper');
        const headerHeight = header?.offsetHeight || 0;

        // Calculate scroll position with offset
        // Use a generous offset to ensure we land in a stable scroll zone
        const targetRect = targetElement.getBoundingClientRect();
        const absoluteTargetTop = targetRect.top + window.scrollY;
        const targetPosition = Math.round(absoluteTargetTop - headerHeight - 24);

        // Use scrollTo with smooth behavior
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Set focus to target for accessibility after scroll completes
        setTimeout(() => {
          targetElement.setAttribute('tabindex', '-1');
          targetElement.focus({ preventScroll: true });
        }, 500);
      }
    });
  });
});
